import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useOnboarding } from './OnboardingProvider';
import { ArrowRight, ArrowLeft, X, Play, CheckCircle, RotateCcw } from 'lucide-react';

export const OnboardingTour: React.FC = () => {
  const {
    currentStep,
    steps,
    isOnboardingActive,
    completeStep,
    skipOnboarding,
    restartOnboarding,
    nextStep,
    previousStep,
  } = useOnboarding();
  const navigate = useNavigate();

  if (!isOnboardingActive) {
    return null;
  }

  const currentStepData = steps[currentStep];
  if (!currentStepData) {
    return null;
  }

  const progress = ((currentStep + 1) / steps.length) * 100;
  const completedSteps = steps.filter(step => step.completed).length;

  const handleTryIt = () => {
    completeStep(currentStepData.id);
    if (currentStepData.href) {
      navigate(currentStepData.href);
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 w-80 sm:w-96">
      <Card className="border-2 border-primary shadow-lg">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Getting Started</CardTitle>
            <Button variant="ghost" size="sm" onClick={skipOnboarding} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                Step {currentStep + 1} of {steps.length}
              </Badge>
              <Badge variant="outline">{completedSteps} completed</Badge>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold text-base mb-2">{currentStepData.title}</h3>
            <p className="text-sm text-muted-foreground">{currentStepData.description}</p>
          </div>

          {currentStepData.completed && (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm font-medium">Step completed!</span>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={previousStep}
              disabled={currentStep === 0}
              className="flex-1"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>

            {!currentStepData.completed ? (
              <Button size="sm" onClick={handleTryIt} className="flex-1">
                <Play className="h-4 w-4 mr-1" />
                {currentStepData.href ? 'Go There' : 'Got It'}
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={nextStep}
                disabled={currentStep === steps.length - 1}
                className="flex-1"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>

          <div className="pt-2 border-t flex gap-2">
            <Button variant="ghost" size="sm" onClick={skipOnboarding} className="flex-1 text-xs">
              Skip Tour
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={restartOnboarding}
              className="flex-1 text-xs"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Restart
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
